import { CUSTOM_ERROR_MESSAGES } from './custom-error-messages';

function isErrorCode(error: string): boolean {
    if (!error) {
        return false;
    }
    if (error.startsWith('ERR_')) {
        return true;
    }
    if (error === error.toUpperCase() && error.includes('_') && !error.includes(' ')) {
        return true;
    }
    return false;
}

// Formats a BE error code into a readable "Sentence case" string.
function formatErrorCode(code: string): string {
    // Check if there is a specific custom override for this code
    if (CUSTOM_ERROR_MESSAGES[code]) {
        return CUSTOM_ERROR_MESSAGES[code];
    }

    let formatted = code;

    // Remove ERR_ prefix if it exists
    if (formatted.startsWith('ERR_')) {
        formatted = formatted.substring(4);
    }

    formatted = formatted.replace(/_/g, ' ').toLowerCase();

    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

/**
 * Prettifies a raw backend error message.
 * - Handles `ERR_CODE` strings.
 * - Handles `ERR_CODE; More details...` strings.
 * - Handles objects with an 'error' field.
 * - Leaves "normal" error messages untouched.
 * - Provides a fallback for non-string or empty inputs.
 *
 * @param errorMessage The raw error message from the backend.
 * @returns A formatted, human-readable error string.
 */
export function formatErrorMessage(error: unknown): string {
    const GENERIC_ERROR = 'Something went wrong. Please try again.';

    let inputMessage: string | null = null;

    if (typeof error === 'string') {
        inputMessage = error;
    } else if (error && typeof error === 'object' && 'error' in error && typeof error.error === 'string') {
        inputMessage = error.error;
    }

    if (!inputMessage || !inputMessage.trim()) {
        return GENERIC_ERROR;
    }

    const message = inputMessage.trim();
    const match = message.match(/[;:]/);
    const separatorIndex = match ? match.index! : -1;

    let errorCode: string;
    let errorMessage: string;

    if (separatorIndex !== -1) {
        errorCode = message.substring(0, separatorIndex).trim();
        errorMessage = message.substring(separatorIndex + 1).trim();
    } else {
        errorCode = message;
        errorMessage = '';
    }

    const customMessage = CUSTOM_ERROR_MESSAGES[errorCode];

    if (customMessage) {
        return customMessage;
    }

    let formattedPart1: string;

    // Only format the first part if it looks like an error code
    if (isErrorCode(errorCode)) {
        formattedPart1 = formatErrorCode(errorCode);
    } else {
        formattedPart1 = errorCode;
    }

    // Re-combine if there was a message part
    if (errorMessage) {
        return `${formattedPart1}: ${errorMessage}`;
    }

    return formattedPart1;
}
